// import { entityAggregateSliceActions } from "../slices/entityAggregateSlice";
// import entitySlice, { entitySliceActions } from "../slices/entitySlice";
// import { globalStatesActions } from "../slices/globalStateSlice";
// import { mapScale, queryParams } from "../../util/utl";
// import { POPULATION_CATEGORY } from "../../components/Matrix";
// import { aggSliceActions } from "../slices/aggSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectionsSliceActions } from "../slices/selectionsSlice";
import { aggSliceActions } from "../slices/aggSlice";
import { apiUrl } from "../../configs/api";

// Reads the numeric round from a data row (the API uses round_number).
const rowRound = (row) => {
  const value = row?.round_number ?? row?.round;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

// The DTM API v3 has no lat/lng. Borrow coordinates from the DB rows we already
// have (geography is stable per pcode) so live rows can be plotted on the map.
const attachCoordsByPcode = (liveRows = [], dbRows = [], pcodeKey) => {
  const coordMap = new Map();
  dbRows.forEach((row) => {
    const pcode = row?.[pcodeKey];
    if (pcode && row.latitude != null && row.longitude != null && !coordMap.has(pcode)) {
      coordMap.set(pcode, { latitude: row.latitude, longitude: row.longitude });
    }
  });
  return liveRows.map((row) => {
    const coords = coordMap.get(row?.[pcodeKey]);
    return coords ? { ...row, ...coords } : row;
  });
};

// Fallback: when the DB has no rows for the requested round, fetch that round
// live from the DTM API v3 (via our backend proxy) and merge it with the DB
// rows so the map/sidebar can show the isolated round's figures.
const fetchLiveFallback = async ({
  level,
  pcodeKey,
  requestBody,
  dbRows,
  round,
}) => {
  try {
    const response = await fetch(apiUrl("/dtm_cdw/live"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ level, ...requestBody }),
    });
    if (!response.ok) {
      throw new Error(`Live API error: ${response.status}`);
    }
    const liveRows = await response.json();
    const withCoords = attachCoordsByPcode(liveRows, dbRows, pcodeKey);
    console.log(
      `[DTM] No DB data for round ${round} — using live API data:`,
      withCoords
    );
    return withCoords;
  } catch (error) {
    console.error("Failed to fetch live DTM API data:", error);
    return [];
  }
};

export const getAdmin1 = (iso3, year, reason, operation) => {
  //   const dispatch = useDispatch();
  return async (dispatch, getState) => {
    async function fetchData(iso3) {
      try {
        const urlencoded = new URLSearchParams();
        urlencoded.append("requestType", "dtm_api_viz_admin_1");
        urlencoded.append("admin_0_pcode", iso3);
        urlencoded.append("year", year);
        // Intentionally NOT filtering by operation here: we fetch all operations
        // so the UI knows which operations have data (to gate the dropdown), and
        // the client-side memos filter by the selected operation for display.
        const trimmedReason = (reason || "").trim();
        if (trimmedReason && trimmedReason.toLowerCase() !== "all") {
          urlencoded.append("reason_for_displacement", trimmedReason);
        }
        // urlencoded.append("operation", operation);
        const response = await fetch(apiUrl("/dtm_cdw/get"), {
          method: "POST",
          body: urlencoded,
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        });

        if (!response.ok) {
          throw new Error(`An error occured: ${response.status}`);
        }

        const data = await response.json();
        // console.log(data);

        dispatch(aggSliceActions.setAdmin1(data));

        // Live-API fallback: if a specific round is requested but the DB has no
        // rows for it, fetch that round live and merge (coords joined by pcode).
        const roundFilter = getState()?.selection?.roundFilter;
        const selectedOperation =
          operation ?? getState()?.selection?.operation;
        if (roundFilter != null) {
          const hasRound =
            Array.isArray(data) &&
            data.some((row) => rowRound(row) === Number(roundFilter));
          if (!hasRound) {
            const liveRows = await fetchLiveFallback({
              level: "admin1",
              pcodeKey: "admin_1_pcode",
              dbRows: Array.isArray(data) ? data : [],
              round: roundFilter,
              requestBody: {
                admin_0_pcode: iso3,
                year,
                round: roundFilter,
                operation: selectedOperation,
              },
            });
            if (liveRows.length) {
              dispatch(
                aggSliceActions.setAdmin1([
                  ...(Array.isArray(data) ? data : []),
                  ...liveRows,
                ])
              );
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch country matrix data:", error);
      }
    }

    fetchData(iso3);
  };
};

export const getAdmin2 = (iso3, year, reason, operation) => {
  //   const dispatch = useDispatch();
  return async (dispatch, getState) => {
    async function fetchData(iso3) {
      try {
        const urlencoded = new URLSearchParams();
        urlencoded.append("requestType", "dtm_api_viz_admin_2");
        urlencoded.append("admin_1_pcode", iso3);
        urlencoded.append("year", year);
        // See getAdmin1: fetch all operations; the UI gates + filters client-side.
        const trimmedReason = (reason || "").trim();
        if (trimmedReason && trimmedReason.toLowerCase() !== "all") {
          urlencoded.append("reason_for_displacement", trimmedReason);
        }
        // urlencoded.append("operation", operation);
        const response = await fetch(apiUrl("/dtm_cdw/get"), {
          method: "POST",
          body: urlencoded,
          headers: {
            "Content-Type": "application/x-www-form-urlencoded"
          },
        });

        if (!response.ok) {
          throw new Error(`An error occured: ${response.status}`);
        }

        const data = await response.json();

        dispatch(aggSliceActions.setAdmin2(data));

        // Live-API fallback (see getAdmin1). iso3 here is the admin_1_pcode.
        const roundFilter = getState()?.selection?.roundFilter;
        const selectedOperation =
          operation ?? getState()?.selection?.operation;
        if (roundFilter != null) {
          const hasRound =
            Array.isArray(data) &&
            data.some((row) => rowRound(row) === Number(roundFilter));
          if (!hasRound) {
            const liveRows = await fetchLiveFallback({
              level: "admin2",
              pcodeKey: "admin_2_pcode",
              dbRows: Array.isArray(data) ? data : [],
              round: roundFilter,
              requestBody: {
                admin_1_pcode: iso3,
                year,
                round: roundFilter,
                operation: selectedOperation,
              },
            });
            if (liveRows.length) {
              dispatch(
                aggSliceActions.setAdmin2([
                  ...(Array.isArray(data) ? data : []),
                  ...liveRows,
                ])
              );
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch country matrix data:", error);
      }
    }

    fetchData(iso3);
  };
};

// This function is used for only Overall IDPs Observed in the right sidebar
export const getOverallIdps = (iso3) => {
  //   const dispatch = useDispatch();
  return async (dispatch) => {
    async function fetchData(iso3) {
      try {
        const urlencoded = new URLSearchParams();
        urlencoded.append("requestType", "dtm_api_viz_trend_year_reason");
        urlencoded.append("admin_0_pcode", iso3);
        const response = await fetch(apiUrl("/dtm_cdw/get"), {
          method: "POST",
          body: urlencoded,
          headers: {
            "Content-Type": "application/x-www-form-urlencoded"
          },
        });

        if (!response.ok) {
          throw new Error(`An error occured: ${response.status}`);
        }

        const data = await response.json();
        dispatch(selectionsSliceActions.setOverallIdpsTrend(data));
      } catch (error) {
        console.error("Failed to fetch country matrix data:", error);
      }
    }

    fetchData(iso3);
  };
};
